/**
 * Socle PUR du formulaire de devis : valeurs autorisées, champs, validation,
 * et annotations destinées aux agents.
 *
 * Séparé de `devis.ts` pour une raison qui n'est pas cosmétique : `devis.ts`
 * importe le client Supabase, dont la seule construction exige des variables
 * d'environnement. Tout ce qui l'importe hérite donc de cette dépendance —
 * un test unitaire, un script de build, et surtout la Netlify Function qui
 * écrira les demandes venues d'un agent.
 *
 * Ce fichier n'importe rien qui ait un effet de bord. Il est lisible par le
 * client, par Node, par un test et par une fonction serveur, et c'est ce qui
 * permet à `request_quote` de réutiliser LA validation du formulaire humain
 * plutôt que d'en entretenir une seconde, qui divergerait.
 *
 * `devis.ts` réexporte tout ce qui suit : les imports existants ne changent pas.
 */

// ---------------------------------------------------------------------------
// Les valeurs autorisées
//
// `valeur` est ce qui part en base, `libelle` ce que le visiteur lit. Les deux
// ne doivent jamais être confondus : le formulaire de l'accueil stockait le
// libellé et le traduisait à l'envoi par un tableau de correspondance. Corriger
// une espace insécable dans un libellé aurait suffi à faire basculer tous les
// volumes sur « unknown », sans aucun message d'erreur.
//
// Ces listes sont aussi la source des contraintes CHECK de la table
// `devis_requests` : toute valeur ajoutée ici doit l'être là-bas, sinon la
// demande est refusée par la base et perdue.
// ---------------------------------------------------------------------------
export interface OptionDevis {
  valeur: string;
  libelle: string;
}

export const SERVICES: readonly OptionDevis[] = [
  { valeur: 'demenagement', libelle: 'Déménagement' },
  { valeur: 'garde-meuble', libelle: 'Garde-Meubles' },
  { valeur: 'international', libelle: 'International' },
  { valeur: 'monte-meubles', libelle: 'Monte-Meubles' },
] as const;

export const VOLUMES: readonly OptionDevis[] = [
  { valeur: '<20', libelle: '< 20m³' },
  { valeur: '20-50', libelle: '20 - 50m³' },
  { valeur: '50-100', libelle: '50 - 100m³' },
  { valeur: '>100', libelle: '> 100m³' },
  { valeur: 'unknown', libelle: 'Je ne sais pas' },
] as const;

/**
 * Repère de logement par tranche de volume.
 *
 * Le bloc « Estimation rapide » du hero proposait SA propre liste de six
 * volumes — « Studio / 1 pièce (10–20 m³) », « Volume sur mesure »… — sans
 * rapport avec les cinq valeurs acceptées par la base. Ce qu'on y choisissait
 * ne pouvait donc pas être repris par le formulaire de devis : il n'existait
 * aucune correspondance, et en écrire une à la main aurait été une septième
 * liste à tenir.
 *
 * Les VALEURS sont celles ci-dessus, partout. Le repère n'est qu'une aide à la
 * lecture, accolée au libellé là où l'on choisit avant d'avoir mesuré.
 */
export const REPERES_VOLUME: Record<string, string> = {
  '<20': 'studio ou 1 pièce',
  '20-50': '2 à 3 pièces',
  '50-100': '4 pièces ou grande maison',
  '>100': 'très grand volume',
  'unknown': 'à estimer ensemble',
};

/**
 * Libellé d'une tranche de volume avec son repère de logement :
 * « 20 - 50m³ — 2 à 3 pièces ». C'est ce que le formulaire AFFICHE ; la
 * valeur envoyée reste `valeur`, celle de la contrainte CHECK.
 */
export const libelleAvecRepere = (option: OptionDevis): string => {
  const repere = REPERES_VOLUME[option.valeur];
  return option.valeur === 'unknown' || !repere ? option.libelle : `${option.libelle} — ${repere}`;
};

/** Libellé lisible d'une valeur, pour le courriel de notification. */
export const libelleDe = (options: readonly OptionDevis[], valeur: string): string =>
  options.find((o) => o.valeur === valeur)?.libelle ?? valeur;

// ---------------------------------------------------------------------------
// Les champs
// ---------------------------------------------------------------------------
export interface ChampsDevis {
  service: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  cityFrom: string;
  cityTo: string;
  date: string;
  volume: string;
  message: string;
  privacy: boolean;
}

export const CHAMPS_VIDES: ChampsDevis = {
  service: SERVICES[0].valeur,
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  cityFrom: '',
  cityTo: '',
  date: '',
  volume: '',
  message: '',
  privacy: false,
};

export type ErreursDevis = Partial<Record<keyof ChampsDevis, string>>;

// ---------------------------------------------------------------------------
// Report d'un formulaire vers un autre
//
// D4 du plan correctif, et le trou le plus coûteux du parcours : le bloc
// « Estimation rapide » du hero portait trois champs — volume, date, courriel —
// qui n'appartenaient à AUCUN <form>, et son bouton était un simple lien vers
// /contact-devis. Une personne, ou un agent, remplissait les trois champs,
// cliquait, et arrivait sur un formulaire vide. Rien ne signalait la perte.
//
// Les valeurs voyagent désormais dans l'URL, et la page d'arrivée les
// reprend. Ce qui est saisi une fois n'est plus saisi deux fois — c'est aussi
// vrai pour un humain que pour un agent.
//
// Seuls des champs NON sensibles voyagent : une URL se retrouve dans
// l'historique, dans le Referer et dans les journaux du serveur. Le nom, le
// téléphone et les adresses n'y ont pas leur place ; le courriel y est admis
// parce que c'est le seul moyen de ne pas le redemander, et parce que la
// personne vient elle-même de le saisir sur la page précédente.
// ---------------------------------------------------------------------------

/** Champs autorisés à transiter par l'URL. Volontairement court. */
export const CHAMPS_REPORTABLES = ['volume', 'date', 'email', 'service'] as const;
export type ChampReportable = (typeof CHAMPS_REPORTABLES)[number];

/** Chaîne de requête pour /contact-devis, à partir de valeurs partielles. */
export function parametresDeReport(valeurs: Partial<Pick<ChampsDevis, ChampReportable>>): string {
  const params = new URLSearchParams();
  for (const champ of CHAMPS_REPORTABLES) {
    const valeur = valeurs[champ];
    if (typeof valeur === 'string' && valeur.trim()) params.set(champ, valeur.trim());
  }
  const chaine = params.toString();
  return chaine ? `?${chaine}` : '';
}

/**
 * Lecture inverse, à l'arrivée.
 *
 * Chaque valeur est VALIDÉE contre les énumérations avant d'être retenue : une
 * URL est une entrée utilisateur comme une autre, et un `?volume=<script>`
 * pré-remplirait sinon le formulaire avec n'importe quoi. Une valeur non
 * reconnue est ignorée, jamais recopiée.
 */
export function reportDepuisUrl(recherche: string): Partial<ChampsDevis> {
  const params = new URLSearchParams(recherche);
  const retenu: Partial<ChampsDevis> = {};

  const volume = params.get('volume');
  if (volume && VOLUMES.some((v) => v.valeur === volume)) retenu.volume = volume;

  const service = params.get('service');
  if (service && SERVICES.some((s) => s.valeur === service)) retenu.service = service;

  const date = params.get('date');
  if (date && /^\d{4}-\d{2}-\d{2}$/.test(date)) retenu.date = date;

  const email = params.get('email');
  if (email && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) retenu.email = email;

  return retenu;
}

/**
 * Ce qu'un agent doit comprendre de chaque champ, pour le remplir juste.
 *
 * Source unique, pour la raison qui a fait naître ce fichier : les trois
 * formulaires du site portaient déjà le même `toolname` et la même
 * `tooldescription`, recopiés à la main dans trois JSX différents. Deux d'entre
 * eux n'avaient AUCUNE description de champ — un agent y remplissait donc
 * « ville de départ » et « ville d'arrivée » au jugé, avec une chance sur deux
 * de les intervertir.
 *
 * ⚠️ 150 caractères maximum par description : c'est une limite dure de WebMCP,
 * au-delà de laquelle la valeur est tronquée par le navigateur. Le test
 * tests/budgets-webmcp.test.ts échoue si une entrée la dépasse.
 *
 * Une description dit ce qu'ATTEND le champ, pas ce qu'il est. « Adresse email
 * pour recevoir le devis » se remplit ; « champ email » ne se remplit pas.
 */
/**
 * Budget d'une description de paramètre, en caractères.
 *
 * Au-delà, le navigateur peut écarter l'annotation SANS RIEN DIRE : le champ
 * réapparaît alors dans le schéma dépourvu de `description`, et l'audit
 * signale une description manquante sur un élément qui en porte une. Le budget
 * est donc une contrainte dure, pas une recommandation de style.
 */
export const BUDGET_DESCRIPTION_PARAM = 150;

export const DESCRIPTIONS_AGENT: Record<keyof ChampsDevis, string> = {
  // D3 du plan correctif. Le choix Déménagement / Garde-Meubles était fait par
  // deux <button type="button"> hors du contrat de formulaire : la
  // `tooldescription` promettait les deux prestations, et le champ qui les
  // porte était invisible pour un agent. Il est désormais un vrai groupe de
  // boutons radio, avec sa description.
  service: 'Prestation souhaitée.',
  // D2 du plan correctif, et le blocage DUR du parcours : la case de
  // consentement n'avait aucune description. Un agent ne pouvait pas savoir
  // qu'elle existait, encore moins qu'elle était obligatoire — il remplissait
  // tout le reste et la soumission échouait sans qu'il comprenne pourquoi.
  privacy: 'Acceptation de la politique de confidentialité. Obligatoire : sans elle la demande ne peut pas être envoyée',
  firstName: 'Prénom de la personne qui déménage',
  lastName: 'Nom de famille',
  email: 'Adresse email pour recevoir le devis',
  phone: 'Numéro de téléphone, indicatif international accepté',
  cityFrom: 'Adresse ou commune de départ, le logement actuel',
  cityTo: "Adresse ou commune d'arrivée, le logement de destination",
  date: 'Date souhaitée du déménagement, au format AAAA-MM-JJ. Facultative : laisser vide si elle n\'est pas encore fixée',
  volume: 'Volume estimé à déménager.',
  message: "Précisions libres : étage, ascenseur, objets fragiles ou lourds, contraintes d'accès",
};

/**
 * Description de l'outil synthétisé à partir du formulaire, identique sur les
 * trois pages qui le servent.
 *
 * Elle dit ce que le formulaire fait ET ce qu'il ne fait pas. C'est la règle
 * la plus utile de la rédaction d'outils : sans la seconde moitié, un agent
 * qui remplit les champs croit avoir envoyé la demande et l'annonce à la
 * personne, qui n'attendra jamais de réponse.
 *
 * ⚠️ 500 caractères maximum. Voir tests/budgets-webmcp.test.ts.
 */
export const DESCRIPTION_OUTIL_DEVIS =
  'Prépare une demande de devis gratuit pour un déménagement ou un garde-meubles chez Déménagements Gramme. ' +
  'Le formulaire est rempli mais reste soumis par la personne : renseigner les champs ne crée aucune demande.';

export const NOM_OUTIL_DEVIS = 'demander_devis_demenagement';


/**
 * Validation commune.
 *
 * La date de déménagement n'est pas exigée. Les trois formulaires la
 * rendaient obligatoire — /contact l'affichait même sans astérisque tout en la
 * refusant vide, une étiquette qui mentait. Or beaucoup de gens demandent un
 * devis AVANT d'avoir une date : c'est le champ le plus susceptible de faire
 * abandonner, et le seul qui n'apporte rien pour rappeler quelqu'un. La colonne
 * est nullable en base depuis l'origine.
 */
export function validerDevis(
  form: ChampsDevis,
  options: { libelleVilles?: 'ville' | 'adresse' } = {},
): ErreursDevis {
  const { libelleVilles = 'ville' } = options;
  const depart = libelleVilles === 'adresse' ? 'Adresse de départ requise' : 'Ville de départ requise';
  const arrivee = libelleVilles === 'adresse' ? "Adresse d'arrivée requise" : "Ville d'arrivée requise";

  const e: ErreursDevis = {};
  if (!form.firstName.trim()) e.firstName = 'Prénom requis';
  if (!form.lastName.trim()) e.lastName = 'Nom requis';
  if (!form.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = 'Email invalide';

  // Le téléphone n'était testé que sur le non-vide : « 04 » passait. C'est le
  // canal de rappel principal, un numéro inexploitable rend la demande morte.
  // La règle reste large à dessein — indicatifs, espaces, points, tirets et
  // parenthèses sont admis — elle n'écarte que ce qui ne peut pas être un
  // numéro.
  const chiffres = form.phone.replace(/[^\d]/g, '');
  if (!form.phone.trim()) e.phone = 'Téléphone requis';
  else if (chiffres.length < 8) e.phone = 'Numéro incomplet';

  if (!form.cityFrom.trim()) e.cityFrom = depart;
  if (!form.cityTo.trim()) e.cityTo = arrivee;
  if (!form.privacy) e.privacy = 'Vous devez accepter la politique';
  return e;
}

/**
 * Description d'un champ à choix, avec les valeurs QUE CE FORMULAIRE accepte.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * POURQUOI CETTE FONCTION EXISTE
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * `DESCRIPTIONS_AGENT.service` énumérait ses valeurs en dur :
 *
 *     'Prestation souhaitée. Valeurs acceptées : demenagement, garde-meuble,
 *      international, monte-meubles'
 *
 * Or les trois formulaires du site n'exposent pas le même sous-ensemble. Celui
 * du hero n'en rend que deux. Un agent lisait donc une promesse de quatre
 * valeurs et un `enum` qui en contenait deux : la troisième qu'il essayait
 * était refusée, sans que rien dans le contrat ne l'ait laissé prévoir.
 *
 * C'est la faute que ce chantier combat au complet — une liste écrite deux
 * fois — commise dans le texte censé décrire l'autre. La corriger à la main
 * l'aurait ramenée au premier formulaire dont on change les options.
 *
 * La description est donc DÉRIVÉE du tableau qui rend les options. Les deux ne
 * peuvent plus diverger, parce qu'il n'y en a plus qu'un.
 */
export function descriptionDuChoix(
  cle: 'service' | 'volume',
  options: readonly OptionDevis[],
): string {
  const texte = `${DESCRIPTIONS_AGENT[cle]} Valeurs acceptées : ${options
    .map((o) => o.valeur)
    .join(', ')}`;
  // Silencieux en production — une description tronquée vaut mieux qu'une page
  // qui refuse de se rendre — mais le test de contrat, lui, échoue dessus.
  return texte;
}
